import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useMediaQuery } from "@/Utils/useMediaQuery";
import { CardView } from "@/components/v2/CardView";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";

import { useCallback, useContext, useMemo, useState } from "react";

import { HomePageContext } from "../../context";
import { ColumnHeader } from "./components/columnHeader";
import { KEY_NUMBERS_COLUMNS } from "./constants";
import { SortDir } from "./types";
import { getKeyNumbersByCategoryRows } from "./utils";

export const KeyNumbersPerCategory: React.FC = () => {
  const { timeFilter, selectedTenantCategoryIds } = useContext(HomePageContext);
  const { currentTerminal } = useTerminalsState();
  const { tenantCategories } = useCommonEntitiesStore();

  const [sortKey, setSortKey] = useState<string>("rate");
  const [sortDir, setSortDir] = useState<SortDir>(SortDir.DESC);

  const hideTenantsColumn = useMediaQuery("(max-width: 1399px)");

  const { data, isLoading } = trpc.dwh.customers.get.useQuery(
    {
      ...(timeFilter || defaultTimeFilter),
      terminalId: currentTerminal?.id || "",
      tenantCategories: selectedTenantCategoryIds || [],
    },
    {
      queryKeyHashFn: () =>
        [
          "customers",
          currentTerminal?.id,
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
          selectedTenantCategoryIds,
        ].toString(),
      enabled: !!timeFilter && !!currentTerminal,
    },
  );

  const rows = useMemo(
    () =>
      getKeyNumbersByCategoryRows({
        sortKey,
        sortDir,
        data,
        tenantCategories,
      }),
    [sortKey, sortDir, data, tenantCategories],
  );

  const columns = useMemo(
    () =>
      hideTenantsColumn
        ? KEY_NUMBERS_COLUMNS.filter((col) => col.key !== "nrTenants")
        : KEY_NUMBERS_COLUMNS,
    [hideTenantsColumn],
  );

  const onSort = useCallback(
    (key: string) => {
      if (key === sortKey) {
        setSortDir((prev) =>
          prev === SortDir.ASC ? SortDir.DESC : SortDir.ASC,
        );
      } else {
        setSortKey(key);
        setSortDir(SortDir.DESC);
      }
    },
    [sortKey],
  );

  return (
    <CardView
      title="homePage:keyNumbersPerCategoryTitle"
      subtitle="homePage:keyNumbersPerCategorySubtitle"
    >
      <ViewWithLoader isLoading={isLoading}>
        <table className="mt-[11.55px] w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {columns.map((col) => (
                <ColumnHeader
                  key={col.key}
                  col={col}
                  activeKey={sortKey}
                  sortDir={sortDir}
                  onSort={onSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} className="border-grey2 border-t">
                {columns.map((column) => (
                  <td
                    key={`${row.name}-${column.key}`}
                    className={column.cellClassName}
                  >
                    {column.getFormattedValue(row)} {column.unit}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </ViewWithLoader>
    </CardView>
  );
};
